import { api } from '@/lib/api-client'
import type {
  AreaGroup,
  AreaGroupInput,
  AreaPricingOverview,
  AreaSurcharge,
  GeoPoint,
  SubArea,
  SubAreaInput,
} from '@/types/api'

/**
 * Cairo area pricing: the sub-areas drawn on the map, their groups, each
 * group's surcharge, and whether Cairo is priced by them or as one area.
 *
 * Every write answers with the record it saved, but the screen refetches the
 * overview after each one anyway: making a group primary takes the mark off
 * another, and deleting a group moves its sub-areas, so one record is never the
 * whole story.
 */

const BASE = '/api/admin/area-pricing'

export function fetchAreaPricing(): Promise<AreaPricingOverview> {
  return api.get<AreaPricingOverview>(BASE)
}

export function setAreaPricingEnabled(enabled: boolean): Promise<AreaPricingOverview> {
  return api.put<AreaPricingOverview>(`${BASE}/settings`, { enabled })
}

export function createAreaGroup(input: AreaGroupInput): Promise<AreaGroup> {
  return api.post<AreaGroup>(`${BASE}/groups`, input)
}

export function updateAreaGroup(id: number, input: AreaGroupInput): Promise<AreaGroup> {
  return api.put<AreaGroup>(`${BASE}/groups/${id}`, input)
}

/** Its sub-areas stay on the map, in no group. */
export function deleteAreaGroup(id: number): Promise<void> {
  return api.delete<void>(`${BASE}/groups/${id}`)
}

export function createSubArea(input: SubAreaInput): Promise<SubArea> {
  return api.post<SubArea>(`${BASE}/sub-areas`, input)
}

export function updateSubArea(id: number, input: SubAreaInput): Promise<SubArea> {
  return api.put<SubArea>(`${BASE}/sub-areas/${id}`, input)
}

export function deleteSubArea(id: number): Promise<void> {
  return api.delete<void>(`${BASE}/sub-areas/${id}`)
}

/** What a Cairo trip between two points would carry if priced by sub-area — asked in either mode. */
export function previewAreaSurcharge(
  pickup: GeoPoint,
  dropoff: GeoPoint,
  signal?: AbortSignal,
): Promise<AreaSurcharge> {
  return api.get<AreaSurcharge>(`${BASE}/preview`, {
    query: {
      pickupLat: pickup.lat,
      pickupLng: pickup.lng,
      dropoffLat: dropoff.lat,
      dropoffLng: dropoff.lng,
    },
    signal,
  })
}
